import BaseComponent from '../base-component/base-component'
import { delay, getTransitionDuration } from '../../utils/utils'
import { forwardTransition } from '../../utils/dom'
import { Nullable } from '../../types/common'

class Accordeon extends BaseComponent {
  contentSelector: string
  contentWrapperSelector: string
  content: Nullable<HTMLElement>
  resizeObserver: ResizeObserver
  styles: CSSStyleDeclaration

  constructor() {
    super()

    this.contentSelector = `[data-accordeon-content]`
    this.contentWrapperSelector = `[data-accordeon-content-wrapper]`

    this.content = this.querySelector(this.contentSelector)

    this.resizeObserver = new ResizeObserver(this.handleContentResize)

    if (this.content) {
      this.resizeObserver.observe(this.content)
    }

    this.styles = getComputedStyle(this)
  }

  connectedCallback(): void {
    this.updateMaxHeight()

    if (this.isEditorMode) {
      this.editor.subscribe('BLOCK_SELECT', this.handleBlockSelect)
      this.editor.subscribe('BLOCK_DESELECT', this.handleBlockDeselect)
    }
  }

  disconnectedCallback(): void {
    this.resizeObserver.disconnect()

    if (this.isEditorMode) {
      this.editor.destroy()
    }
  }

  handleContentResize = (): void => {
    this.updateMaxHeight()
  }

  handleBlockSelect = ({ detail: { sectionId, blockId, load } }): void => {
    const contentWrapper = this.querySelector(this.contentWrapperSelector)
    const isCurrentSection = this.dataset.sectionId === sectionId
    const isCurrentBlock = this.dataset.blockId === blockId
    const isTarget = isCurrentSection && isCurrentBlock

    if (isTarget && !this.isExpanded) {
      forwardTransition(contentWrapper, load)
      forwardTransition(this, load)

      this.show().then(() => {
        forwardTransition(contentWrapper, false)
        forwardTransition(this, false)
      })
    }
  }

  handleBlockDeselect = ({ detail: { sectionId, blockId } }): void => {
    const isTarget =
      this.dataset.sectionId === sectionId && this.dataset.blockId === blockId

    if (isTarget && this.isExpanded) {
      this.hide()
    }
  }

  hide(): Promise<any> {
    this.setExpand(false)

    return delay(getTransitionDuration(this.styles))
  }

  show(): Promise<any> {
    this.setExpand(true)

    return delay(getTransitionDuration(this.styles))
  }

  toggle(): void {
    this.setExpand(!this.isExpanded)

    this.emit('toggle', { blockId: this.dataset.blockId })
  }

  updateMaxHeight(): void {
    const accordeon = this.closest<Accordeon>('accordeon-component')
    const content = this.querySelector<HTMLElement>(this.contentSelector)
    const contentWrapper = this.querySelector<HTMLElement>(
      this.contentWrapperSelector,
    )

    if (accordeon && content && contentWrapper) {
      if (accordeon.isExpanded) {
        this.removeAttribute('style')
      }

      const height = Math.ceil(accordeon.isExpanded ? content.scrollHeight : 0)

      contentWrapper.style.maxHeight = `${height}px`
    }
  }

  setExpand(isExpanded: boolean): void {
    this.setAttribute('data-aria-expanded', isExpanded ? 'true' : 'false')

    this.updateMaxHeight()
  }

  get isExpanded(): boolean {
    return this.getAttribute('data-aria-expanded') === 'true'
  }
}

export default Accordeon
